// routes
import NavSection from "components/NavSection";
import useAuth from "hooks/useAuth";
import useContactsSidebarPath from "hooks/useContactsSurface";
import useFirestore from "hooks/useFirestore";
import useWebotAdmin from "hooks/useWebotAdmin";
import useSalesTrainingV2Access from "hooks/useSalesTrainingV2Access";
import { useTranslation } from "react-i18next";
import SvgIconStyle from "../../components/SvgIconStyle";
import {
  DB_EXPLORER_ALLOWED_EMAILS,
  SALES_MAP_ALLOWED_EMAILS,
  SALES_RECRUITING_ADMIN_EMAILS,
  SALES_TRAINING_ALLOWED_EMAILS
} from "../../@types/sharedSchema";
import { PATH_DASHBOARD } from "../../routes/paths";

// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle src={`/static/icons/navbar/${name}`} sx={{ width: "100%", height: "100%" }} />
);

// Get icons from other directories
const getFileIcon = (name: string) => (
  <SvgIconStyle src={`/static/icons/file/${name}`} sx={{ width: "100%", height: "100%" }} />
);

const ICONS = {
  blog: getIcon("ic_blog.svg"),
  cart: getIcon("ic_cart.svg"),
  chat: getIcon("ic_chat.svg"),
  mail: getIcon("ic_mail.svg"),
  user: getIcon("ic_user.svg"),
  kanban: getIcon("ic_kanban.svg"),
  banking: getIcon("ic_banking.svg"),
  calendar: getIcon("ic_calendar.svg"),
  ecommerce: getIcon("ic_ecommerce.svg"),
  analytics: getIcon("ic_analytics.svg"),
  dashboard: getIcon("ic_dashboard.svg"),
  booking: getIcon("ic_booking.svg"),
  house: getIcon("house.png"),
  houseSearch: getIcon("house_search.png"),
  phone: getIcon("phone.png"),
  import: getIcon("import.png"),
  signature: getIcon("signature.png"),
  pdf: getFileIcon("file_type_pdf.svg"),
  documentTemplate: getIcon("ic_banking.svg"),
  formBuilder: getIcon("ic_kanban.svg"),
  sendSignature: getIcon("ic_mail.svg"),
  manageSignature: getIcon("ic_user.svg"),
  instructions: getIcon("ic_blog.svg"),
  digitalTemplates: getIcon("digital_templates.svg"),
  digitalInstruction: getIcon("digital_instruction.svg"),
  digitalGenerator: getIcon("digital_generator.svg"),
  digitalSignature: getIcon("digital_signature.svg")
};

type LocalizedSidebarConfigProps = {
  isShow: boolean;
};

export default function LocalizedSidebarConfig({ isShow }: LocalizedSidebarConfigProps) {
  const { userSettings } = useFirestore();
  const { isWebotAdmin } = useWebotAdmin();
  const { user } = useAuth();
  const isDbExplorerOwner = (DB_EXPLORER_ALLOWED_EMAILS as readonly string[]).includes(
    (user?.email ?? "").toLowerCase()
  );
  // Sales map access is its own allowlist, deliberately NOT the webot-admin list (see
  // docs/rules/sales-map.md) — an allowed email must see this entry even when they are
  // not a webot admin, so it cannot simply ride the `isWebotAdmin` gate below.
  const isSalesMapOwner = (SALES_MAP_ALLOWED_EMAILS as readonly string[]).includes(
    (user?.email ?? "").toLowerCase()
  );
  // Sales training portal: its own allowlist too (docs/rules/sales-training.md in webot-server) — reps added there
  // must not get the sales map, and neither list is the webot-admin list.
  const isSalesTrainingOwner = (SALES_TRAINING_ALLOWED_EMAILS as readonly string[]).includes(
    (user?.email ?? "").toLowerCase()
  );
  // Sales onboarding portal v2: the owners always, plus whoever they added from the page (asked from the server).
  const { canView: canViewSalesTrainingV2 } = useSalesTrainingV2Access();
  // Sales recruiting (the job applicants for the sales team): its own allowlist again, and the narrowest —
  // the sales reps are on the two lists above, and who applied to their team is not theirs to read.
  const isSalesRecruitingOwner = (SALES_RECRUITING_ADMIN_EMAILS as readonly string[]).includes(
    (user?.email ?? "").toLowerCase()
  );

  const { t } = useTranslation();

  const isRealEstateUser = userSettings?.crmType === "realEstate";
  const hasSignatureFeatureFlag = userSettings?.featureFlagsV2?.includes("signature") ?? false;
  // Preview feature — the Facebook-leads entry stays hidden unless explicitly granted.
  const hasFacebookLeadsFeature = userSettings?.advancedFeatures?.includes("facebookLeads") ?? false;
  // Preview feature — the Facebook group-posting entry stays hidden unless explicitly granted.
  const hasFacebookGroupPostingFeature =
    userSettings?.advancedFeatures?.includes("facebookGroupPosting") ?? false;
  // Preview feature — the official-WhatsApp entry stays hidden unless explicitly granted.
  const hasOfficialWhatsappFeature =
    userSettings?.advancedFeatures?.includes("officialWhatsapp") ?? false;
  // NOTE: the Postgres-backed CRM page (/dashboard/crm) deliberately has NO sidebar entry
  // of its own. Both surfaces are called "לקוחות" now, so two entries by that name would be
  // a riddle rather than a choice. Instead the ONE "לקוחות" entry follows the surface the
  // user last opened (`useContactsSidebarPath`) — otherwise a user who moved to the new
  // page would be dragged back to the old one by their own sidebar on every click.
  // Crossing over is ContactsPageSwitchButton (gated by `canReachCrmPage`) — see
  // components/_dashboard/contacts.
  const contactsPath = useContactsSidebarPath();
  // טופס לידים has NO entry here (Haim, 2026-10-16): it is part of the CRM, its links behind an item
  // in the contacts pages' menus, and the people it brings in are contacts like any other.

  const sidebarConfig = [
    {
      subheader: "",
      items: [
        {
          title: t("sidebarPageTitles.general.main"),
          path: PATH_DASHBOARD.general.main,
          icon: ICONS.analytics
        },
        {
          title: t("sidebarPageTitles.general.contacts"),
          path: contactsPath,
          icon: ICONS.user
        },
        {
          title: t("sidebarPageTitles.whatsAppWeb.sendMessages"),
          path: PATH_DASHBOARD.whatsAppWeb.sendMessages,
          icon: ICONS.mail
        },
        {
          title: t("sidebarPageTitles.general.newCalls"),
          path: PATH_DASHBOARD.general.newCalls,
          icon: ICONS.phone
        },
        {
          title: t("sidebarPageTitles.autoreplyBotSettings.autoreplyBot"),
          path: PATH_DASHBOARD.autoreplyBotSettings.autoreplyBot,
          icon: ICONS.chat
        },
        {
          title: t("sidebarPageTitles.calendar"),
          path: PATH_DASHBOARD.general.calendar,
          icon: ICONS.calendar
        },
        ...(isRealEstateUser
          ? [
              {
                title: t("sidebar.listings"),
                // Parent click lands on the listings-management page —
                // the closest thing to a "home" for this section. Mirrors
                // how the signatures group nests under its own root path.
                path: PATH_DASHBOARD.general.listings,
                icon: ICONS.house,
                children: [
                  {
                    title: t("sidebarPageTitles.listings"),
                    path: PATH_DASHBOARD.general.listings,
                    icon: ICONS.house
                  },
                  {
                    title: t("sidebarPageTitles.listingsBoard"),
                    path: PATH_DASHBOARD.general.listingsBoard,
                    icon: ICONS.houseSearch
                  },
                  {
                    title: t("sidebarPageTitles.listingsMatching"),
                    path: PATH_DASHBOARD.general.listingsMatch,
                    icon: ICONS.houseSearch
                  },
                  {
                    title: t("sidebarPageTitles.rentalToSale"),
                    path: PATH_DASHBOARD.general.rentalToSale,
                    icon: ICONS.houseSearch
                  },
                  {
                    title: t("sidebarPageTitles.linksControlPanel"),
                    path: PATH_DASHBOARD.general.linksControlPanel,
                    icon: ICONS.booking
                  },
                  {
                    title: t("creativesGenerator.pageTitle"),
                    path: PATH_DASHBOARD.general.creativesGenerator,
                    icon: ICONS.digitalGenerator
                  },
                  {
                    title: t("makeovers.pageTitle", "עיצובי AI לנכסים"),
                    path: PATH_DASHBOARD.general.makeovers,
                    icon: ICONS.digitalGenerator
                  },
                  // Preview feature — hidden for everyone until the `facebookLeads`
                  // advanced feature is granted.
                  ...(hasFacebookLeadsFeature
                    ? [
                        {
                          title: "לידים מפייסבוק",
                          path: PATH_DASHBOARD.general.facebookLeads,
                          icon: ICONS.houseSearch
                        }
                      ]
                    : []),
                  // Preview feature — hidden until the `facebookGroupPosting` feature is granted.
                  // Realtors find it here, beside their listings; every other account gets the
                  // top-level entry below.
                  ...(hasFacebookGroupPostingFeature
                    ? [
                        {
                          title: "פרסום בקבוצות פייסבוק",
                          path: PATH_DASHBOARD.general.fbPosting,
                          icon: ICONS.house
                        }
                      ]
                    : [])
                ]
              }
            ]
          : []),
        // The same page for an account with no listings group (any crmType but realEstate): it posts
        // free posts only, never sees the listings child above (nor the listing row menu's link), and
        // without this entry reaches the page only by typing its URL. TOP-LEVEL, like
        // officialWhatsapp below.
        ...(!isRealEstateUser && hasFacebookGroupPostingFeature
          ? [
              {
                title: "פרסום בקבוצות פייסבוק",
                path: PATH_DASHBOARD.general.fbPosting,
                icon: ICONS.kanban
              }
            ]
          : []),
        // Preview feature — hidden until `officialWhatsapp` is granted. TOP-LEVEL on purpose:
        // buried as a listings child it was effectively invisible (and it isn't a listings
        // concern — non-realEstate accounts can hold a number too).
        ...(hasOfficialWhatsappFeature
          ? [
              {
                // ONE childless entry — the hub page's own top tabs (חיבור/הודעות/תבניות/
                // קמפיינים/קהלים/חסומים לדיוור) replaced the five sidebar children (campaign-UX
                // rewrite, D2). The last two tabs are also reachable by crm-only accounts via
                // the CRM header links — no sidebar entry for them, on purpose.
                title: "WhatsApp רשמי",
                path: PATH_DASHBOARD.general.whatsappOfficial,
                icon: ICONS.chat
              }
            ]
          : []),
        // Single entry — the hub page owns the flow (role quick-pick) and
        // links to the sent/templates pages, which are intentionally not
        // tracked as sidebar items anymore.
        ...(hasSignatureFeatureFlag
          ? [
              {
                title: t("signatures.sidebarHub"),
                path: PATH_DASHBOARD.signatures.root,
                icon: ICONS.digitalSignature
              }
            ]
          : []),
        {
          title: t("sidebarPageTitles.general.importLeads"),
          path: PATH_DASHBOARD.general.importLeads,
          icon: ICONS.import
        },
        ...(isWebotAdmin || isSalesMapOwner || isSalesTrainingOwner || isSalesRecruitingOwner || canViewSalesTrainingV2
          ? [
              {
                title: t("sidebar.admin", "ניהול"),
                path: isWebotAdmin
                  ? PATH_DASHBOARD.admin.creativesGenerator
                  : isSalesMapOwner
                    ? PATH_DASHBOARD.admin.salesMap
                    : isSalesTrainingOwner
                      ? PATH_DASHBOARD.admin.salesTraining
                      : isSalesRecruitingOwner
                        ? PATH_DASHBOARD.admin.salesRecruiting
                        : PATH_DASHBOARD.admin.salesTrainingV2,
                icon: ICONS.dashboard,
                children: [
                  // Every item below is genuinely admin-only; a sales-map-only allowed
                  // email (not a webot admin) must NOT see them.
                  ...(isWebotAdmin
                    ? [
                        {
                          title: t("admin.users.title", "ניהול משתמשים"),
                          path: PATH_DASHBOARD.admin.users,
                          icon: ICONS.user
                        },
                        {
                          title: t("admin.creativesGenerator.pageTitle", "Admin - Creatives Generator"),
                          path: PATH_DASHBOARD.admin.creativesGenerator,
                          icon: ICONS.digitalGenerator
                        },
                        {
                          title: t("admin.training.pageTitle", "Training Materials"),
                          path: PATH_DASHBOARD.admin.training,
                          icon: ICONS.blog
                        },
                        {
                          title: t("adminAiReview.pageTitle", "AI session review"),
                          path: PATH_DASHBOARD.admin.aiReview,
                          icon: ICONS.chat
                        },
                        {
                          title: t("admin.feedbacks.pageTitle", "User Feedbacks"),
                          path: PATH_DASHBOARD.admin.feedbacks,
                          icon: ICONS.chat
                        },
                        {
                          title: t("admin.coins.title", "ניהול טוקנים"),
                          path: PATH_DASHBOARD.admin.coins,
                          icon: ICONS.dashboard
                        },
                        {
                          title: t("admin.signatures.title", "מסמכי חתימה"),
                          path: PATH_DASHBOARD.admin.signatures,
                          icon: ICONS.digitalSignature
                        },
                        {
                          title: t("admin.locations.title", "מיקומי מאסטר"),
                          path: PATH_DASHBOARD.admin.locations,
                          icon: ICONS.dashboard
                        },
                        {
                          title: t("admin.facebookSources.title", "Facebook Sources"),
                          path: PATH_DASHBOARD.admin.facebookSourcesStats,
                          icon: ICONS.chat
                        }
                      ]
                    : []),
                  // Owner-only (DB_EXPLORER_ALLOWED_EMAILS) — hidden even from other webot admins
                  ...(isDbExplorerOwner
                    ? [
                        {
                          title: "DB Explorer",
                          path: PATH_DASHBOARD.admin.dbExplorer,
                          icon: ICONS.analytics
                        }
                      ]
                    : []),
                  // Owner-only (SALES_MAP_ALLOWED_EMAILS) — independent of webot-admin status
                  ...(isSalesMapOwner
                    ? [
                        {
                          title: t("salesMap.sidebarTitle", "מפת משרדים"),
                          path: PATH_DASHBOARD.admin.salesMap,
                          icon: ICONS.houseSearch
                        }
                      ]
                    : []),
                  // The sales onboarding portal v2: the owners and the readers they add from the page (a server-side
                  // list, so the hook asks the server once and remembers). A reader who is nothing else sees ניהול
                  // with this one entry in it (Haim, 2026-10-28: under ניהול, not on its own).
                  ...(canViewSalesTrainingV2
                    ? [
                        {
                          title: t("salesTrainingV2.sidebarTitle", "פורטל המכירות"),
                          path: PATH_DASHBOARD.admin.salesTrainingV2,
                          icon: ICONS.phone
                        }
                      ]
                    : []),
                  // Owner-only (SALES_TRAINING_ALLOWED_EMAILS) — independent of webot-admin status
                  ...(isSalesTrainingOwner
                    ? [
                        {
                          title: t("salesTraining.sidebarTitle", "פורטל מכירות מלא - ישן"),
                          path: PATH_DASHBOARD.admin.salesTraining,
                          icon: ICONS.phone
                        }
                      ]
                    : []),
                  // Owner-only (SALES_RECRUITING_ADMIN_EMAILS) — independent of webot-admin status
                  ...(isSalesRecruitingOwner
                    ? [
                        {
                          title: t("salesRecruiting.sidebarTitle", "מיון מועמדים"),
                          path: PATH_DASHBOARD.admin.salesRecruiting,
                          icon: ICONS.user
                        }
                      ]
                    : [])
                ]
              }
            ]
          : []),
        {
          title: t("sidebarPageTitles.training"),
          path: PATH_DASHBOARD.general.training,
          icon: ICONS.blog
        }
      ]
    }
  ];

  return <NavSection navConfig={sidebarConfig} isShow={isShow} />;
}
